// Time for the screen: wall clocks on a named zone, with Intl only.
//
// The same rules as backend/domain/tz.js, duplicated on purpose (§4.1: the
// browser never imports server code). The screen needs them for two things:
// drawing an instant on the reader's wall, and turning what somebody typed
// («Tuesday, 10:00») into a wall clock + zone the API accepts. The API never
// receives a bare local time.

const formatters = new Map()

function formatterFor(timeZone) {
  let formatter = formatters.get(timeZone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
    formatters.set(timeZone, formatter)
  }
  return formatter
}

export function browserZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid' } catch { return 'Europe/Madrid' }
}

export function isValidZone(zone) {
  if (!zone) return false
  try { formatterFor(zone); return true } catch { return false }
}

export function partsIn(date, timeZone) {
  const out = {}
  for (const part of formatterFor(timeZone).formatToParts(new Date(date))) {
    if (part.type !== 'literal') out[part.type] = Number(part.value)
  }
  return { year: out.year, month: out.month, day: out.day, hour: out.hour % 24, minute: out.minute, second: out.second }
}

function offsetMinutes(date, timeZone) {
  const at = new Date(date)
  const wall = partsIn(at, timeZone)
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second)
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000)
}

/** The instant a wall clock in that zone reads this date and time. */
export function zonedToUtc({ year, month, day, hour = 0, minute = 0 }, timeZone) {
  const target = Date.UTC(year, month - 1, day, hour, minute)
  const before = offsetMinutes(target - 86400000, timeZone)
  const offsets = new Set([before, offsetMinutes(target, timeZone), offsetMinutes(target + 86400000, timeZone)])
  const right = []
  for (const offset of offsets) {
    const instant = target - offset * 60000
    const wall = partsIn(instant, timeZone)
    if (wall.year === year && wall.month === month && wall.day === day && wall.hour === hour && wall.minute === minute) right.push(instant)
  }
  if (right.length) return new Date(Math.min(...right))
  return new Date(target - before * 60000)
}

const pad = n => String(n).padStart(2, '0')

export const formatDate = ({ year, month, day }) => `${year}-${pad(month)}-${pad(day)}`
export const parseDate = value => {
  const [year, month, day] = String(value).split('-').map(Number)
  return { year, month, day }
}
export const isDate = value => /^\d{4}-\d{2}-\d{2}$/.test(String(value ?? ''))
export const isTime = value => /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value ?? ''))

export const dateIn = (date, zone) => formatDate(partsIn(date, zone))
export function timeIn(date, zone) {
  const p = partsIn(date, zone)
  return `${pad(p.hour)}:${pad(p.minute)}`
}
/** Minutes since midnight on that wall. */
export function minutesIn(date, zone) {
  const p = partsIn(date, zone)
  return p.hour * 60 + p.minute
}

export function addDays(value, days) {
  const { year, month, day } = parseDate(value)
  const next = new Date(Date.UTC(year, month - 1, day + days))
  return formatDate({ year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate() })
}
export function daysBetween(a, b) {
  const x = parseDate(a)
  const y = parseDate(b)
  return Math.round((Date.UTC(y.year, y.month - 1, y.day) - Date.UTC(x.year, x.month - 1, x.day)) / 86400000)
}
/** 0 Sunday … 6 Saturday. */
export function weekdayOf(value) {
  const { year, month, day } = parseDate(value)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}
export const atTime = (dateValue, time, zone) => {
  const [hour, minute] = String(time).split(':').map(Number)
  return zonedToUtc({ ...parseDate(dateValue), hour, minute }, zone)
}
export const startOfDate = (value, zone) => zonedToUtc({ ...parseDate(value), hour: 0, minute: 0 }, zone)

/** The Monday on or before a date: weeks start on Monday here. */
export const startOfWeek = value => addDays(value, -((weekdayOf(value) + 6) % 7))

export function addMonths(value, months) {
  const { year, month } = parseDate(value)
  const index = year * 12 + (month - 1) + months
  return formatDate({ year: Math.floor(index / 12), month: (index % 12) + 1, day: 1 })
}

/** The days a view shows, from its anchor date. */
export function rangeFor(view, anchor) {
  if (view === 'day') return { from: anchor, to: anchor, days: [anchor] }
  if (view === 'week') {
    const from = startOfWeek(anchor)
    const days = Array.from({ length: 7 }, (_, i) => addDays(from, i))
    return { from, to: days[6], days }
  }
  // The working week: Monday to Friday of the anchor's week, even from a Sunday.
  if (view === 'workweek') {
    const from = startOfWeek(anchor)
    const days = Array.from({ length: 5 }, (_, i) => addDays(from, i))
    return { from, to: days[4], days }
  }
  // Month: whole weeks around the month, six rows so the grid never jumps.
  const first = formatDate({ ...parseDate(anchor), day: 1 })
  const from = startOfWeek(first)
  const days = Array.from({ length: 42 }, (_, i) => addDays(from, i))
  return { from, to: days[41], days, month: parseDate(anchor).month }
}

/** One step back or forward in a view. */
export function step(view, anchor, direction) {
  if (view === 'day') return addDays(anchor, direction)
  if (view === 'week' || view === 'workweek') return addDays(anchor, 7 * direction)
  return addMonths(anchor, direction)
}

/** Whether an event touches a day on the reader's wall. */
export function onDay(event, day, zone) {
  if (event.allDay) return event.startDate <= day && event.endDate >= day
  const start = startOfDate(day, zone).getTime()
  const end = startOfDate(addDays(day, 1), zone).getTime()
  return new Date(event.startsAt).getTime() < end && new Date(event.endsAt).getTime() > start
}

/** Whether it goes in the all-day strip rather than on the hours. */
export function isStrip(event, zone) {
  if (event.allDay) return true
  // Anything that spans more than a whole day reads as a bar, not a column.
  return (new Date(event.endsAt) - new Date(event.startsAt)) >= 24 * 3600000
    && dateIn(event.startsAt, zone) !== dateIn(new Date(new Date(event.endsAt).getTime() - 1), zone)
}

/**
 * Timed events of one day laid out side by side where they overlap: each gets
 * a top and a height in minutes, and a column out of the columns its cluster
 * needs. What every calendar does, so two meetings at ten are two narrow
 * blocks and not one hidden under the other.
 */
export function layoutDay(events, day, zone) {
  const dayStart = startOfDate(day, zone).getTime()
  const dayEnd = startOfDate(addDays(day, 1), zone).getTime()
  const minutesOfDay = (dayEnd - dayStart) / 60000
  const items = events
    .filter(event => !isStrip(event, zone) && onDay(event, day, zone))
    .map(event => {
      const start = Math.max(new Date(event.startsAt).getTime(), dayStart)
      const end = Math.min(new Date(event.endsAt).getTime(), dayEnd)
      // Minutes on the wall, not elapsed: on the day the clocks change, 10:00
      // is still drawn at the 10:00 line.
      const top = start === dayStart ? 0 : minutesIn(start, zone)
      const bottom = end === dayEnd ? 24 * 60 : Math.max(top + 1, minutesIn(end, zone) || 24 * 60)
      return { event, top, height: Math.max(bottom - top, 15), start, end }
    })
    .sort((a, b) => a.start - b.start || b.end - a.end)

  const out = []
  let cluster = []
  let clusterEnd = -Infinity
  const flush = () => {
    const columns = []
    for (const item of cluster) {
      let column = columns.findIndex(last => last <= item.start)
      if (column === -1) { column = columns.length; columns.push(item.end) } else columns[column] = item.end
      item.column = column
    }
    for (const item of cluster) out.push({ ...item, columns: columns.length })
    cluster = []
  }
  for (const item of items) {
    if (item.start >= clusterEnd && cluster.length) flush()
    cluster.push(item)
    clusterEnd = Math.max(clusterEnd, item.end)
  }
  if (cluster.length) flush()
  return { items: out, minutesOfDay }
}

/** Rounds minutes to the grid a pointer snaps to. */
export const snap = (minutes, grid = 15) => Math.max(0, Math.min(24 * 60, Math.round(minutes / grid) * grid))

export function minutesToTime(minutes) {
  const clean = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)))
  return `${pad(Math.floor(clean / 60))}:${pad(clean % 60)}`
}

export function timeToMinutes(value) {
  const [h, m] = String(value).split(':').map(Number)
  return h * 60 + m
}

/** «09:00 – 10:30» on a wall, in the reader's locale. */
export function timeRange(event, zone, locale) {
  if (event.allDay) return ''
  const format = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone: zone })
  return `${format.format(new Date(event.startsAt))} – ${format.format(new Date(event.endsAt))}`
}

/** The first letter up: «septiembre de 2026» → «Septiembre de 2026». CSS
 *  capitalize would give «Septiembre De 2026». */
export const capitalise = text => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)
